/**
 * Helpers for the Customer Identity Settings module.
 *
 * Two responsibilities:
 *   - Static metadata (data types, input types, scope ordering)
 *   - Value coercion + JSON-safe equality so we know whether an editor
 *     dirties a row and what shape to send back to the API.
 */

export const DATA_TYPES = ['string', 'integer', 'decimal', 'boolean', 'date', 'datetime', 'json'];

export const INPUT_TYPES = [
  'text', 'textarea', 'number', 'decimal', 'switch', 'checkbox',
  'select', 'multiselect', 'radio', 'date', 'datetime', 'time',
  'color', 'password', 'email', 'url', 'json',
];

export const SCOPES = ['global', 'site', 'customer', 'user', 'address'];

export const SCOPE_ORDER = ['global', 'site', 'customer', 'user', 'address'];

/**
 * Best default input type for a given data type. Used when a user picks
 * a data type and we want to pre-select a sensible input control.
 */
export const DEFAULT_INPUT_FOR_DATA_TYPE = {
  string: 'text',
  integer: 'number',
  decimal: 'decimal',
  boolean: 'switch',
  date: 'date',
  datetime: 'datetime',
  json: 'json',
};

/**
 * Some input types only make sense for one or two data types. Used to
 * grey-out/disable options and to validate before saving.
 */
export const INPUT_TYPE_COMPATIBILITY = {
  text: ['string'],
  textarea: ['string'],
  number: ['integer'],
  decimal: ['decimal'],
  switch: ['boolean'],
  checkbox: ['boolean'],
  select: ['string', 'integer', 'decimal'],
  multiselect: ['string', 'integer', 'decimal', 'json'],
  radio: ['string', 'integer', 'decimal'],
  date: ['date'],
  datetime: ['datetime'],
  time: ['string'],
  color: ['string'],
  password: ['string'],
  email: ['string'],
  url: ['string'],
  json: ['json'],
};

export function isInputCompatible(inputType, dataType) {
  const allowed = INPUT_TYPE_COMPATIBILITY[inputType];
  if (!allowed) return true;
  return allowed.includes(dataType);
}

/**
 * Coerce a raw editor value into a JSON-friendly representation matching
 * the declared data type. Returns `null` for empty input on non-boolean
 * types so unset values stay distinguishable from explicit zero/empty.
 */
export function coerceValue(rawValue, dataType, inputType) {
  if (rawValue === undefined) return null;
  if (rawValue === null) return null;

  if (inputType === 'multiselect') {
    if (!Array.isArray(rawValue)) return [];
    return rawValue.map((v) => coerceValue(v, dataType, 'text'));
  }

  switch (dataType) {
    case 'boolean':
      if (typeof rawValue === 'boolean') return rawValue;
      if (typeof rawValue === 'string') return rawValue === 'true' || rawValue === '1';
      return Boolean(rawValue);
    case 'integer': {
      if (rawValue === '' || rawValue === null) return null;
      const n = Number(rawValue);
      if (!Number.isFinite(n)) return null;
      return Math.trunc(n);
    }
    case 'decimal': {
      if (rawValue === '' || rawValue === null) return null;
      const n = Number(rawValue);
      if (!Number.isFinite(n)) return null;
      return n;
    }
    case 'json': {
      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (!trimmed) return null;
        try {
          return JSON.parse(trimmed);
        } catch {
          return rawValue;
        }
      }
      return rawValue;
    }
    case 'date':
    case 'datetime':
    case 'string':
    default: {
      if (rawValue === '') return null;
      return typeof rawValue === 'string' ? rawValue : String(rawValue);
    }
  }
}

/**
 * Convert a stored JSON value to the string/array form an editor input
 * expects. Inverse of `coerceValue` for editing.
 */
export function valueToEditorString(value, inputType, dataType) {
  if (value === undefined || value === null) {
    if (inputType === 'multiselect') return [];
    if (dataType === 'boolean') return false;
    return '';
  }
  if (inputType === 'multiselect') {
    return Array.isArray(value) ? value : [value];
  }
  if (dataType === 'boolean') return Boolean(value);
  if (dataType === 'json') {
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  }
  return typeof value === 'string' ? value : String(value);
}

/**
 * JSON-safe equality used to detect dirty rows in the values editor.
 */
export function valuesEqual(a, b) {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * Group an array of definitions by `group_name`. Returns an array of
 * `{ name, items }` for stable rendering.
 */
export function groupDefinitions(definitions) {
  const buckets = new Map();
  (definitions || []).forEach((def) => {
    const key = def.group_name || 'General';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(def);
  });
  const out = [];
  Array.from(buckets.keys())
    .sort((a, b) => a.localeCompare(b))
    .forEach((name) => {
      const items = buckets
        .get(name)
        .slice()
        .sort((a, b) => {
          const so = (a.sort_order || 0) - (b.sort_order || 0);
          if (so !== 0) return so;
          return (a.name || '').localeCompare(b.name || '');
        });
      out.push({ name, items });
    });
  return out;
}
